import { TicketSourcePlatform } from "~/projects/help-desk/types/enums/ticketSourcePlatform";
import { TicketSourceType } from "~/projects/help-desk/types/enums/ticketSourceType";
import { TicketPriority } from "~/projects/help-desk/types";
import { CallStatus } from "~/types";
export default function helper() {
  function getBadgeColorForPriority(priority: TicketPriority) {
    let color = "svgs/";
    switch (priority) {
      case TicketPriority.LOW:
        color += "green";
        break;
      case TicketPriority.MEDIUM:
        color += "blue";
        break;
      case TicketPriority.NORMAL:
        color += "yellow";
        break;
      case TicketPriority.CRITICAL:
      case TicketPriority.URGENT:
        color += "red";
        break;
    }
    return color;
  }
  function getPlatformIcon(platform: TicketSourcePlatform): string {
    const iconPath = "/images/social_media_icons/";
    let icon = "meta.png";
    switch (platform) {
      case TicketSourcePlatform.INSTAGRAM:
        icon = "instagram.ico";
        break;
      case TicketSourcePlatform.PAGE:
        icon = "facebook.ico";
        break;
      case TicketSourcePlatform.WHATSAPP:
        icon = "whatsapp_cloud.png";
        break;
    }
    return iconPath + icon;
  }
  function getDeliveryIntegrationIcon(partner: string): string {
    const iconPath = "/images/delivery_integration_icons/";
    let icon = "";
    switch (partner) {
      case "pathao":
        icon = "pathao.ico";
        break;
      case "steadfast":
        icon = "steadfast.ico";
        break;
      case "karbari":
        icon = "karbari.ico";
        break;
      case "ecomdrive":
        icon = "ecomdrive.ico";
        break;
      case "nuport":
        icon = "nuport.ico";
        break;
      case "bizmotion":
        icon = "bizmotion.ico";
        break;
    }
    return iconPath + icon;
  }

  function getSourceTypeIcon(
    sourcePlatform: TicketSourcePlatform,
    sourceType: TicketSourceType,
  ) {
    const iconPath = "/images/social_media_icons/";
    let icon = "";

    if (sourcePlatform === TicketSourcePlatform.PAGE) {
      switch (sourceType) {
        case TicketSourceType.COMMENT:
          icon = "facebook.ico";
          break;

        case TicketSourceType.MESSAGE:
          icon = "messenger.ico";
          break;
      }
    } else if (sourcePlatform === TicketSourcePlatform.INSTAGRAM) {
      switch (sourceType) {
        case TicketSourceType.MESSAGE:
          icon = "instagram.ico";
          break;
        case TicketSourceType.COMMENT:
          icon = "instagram_comment.ico";
          break;
      }
    } else if (sourcePlatform === TicketSourcePlatform.WHATSAPP) {
      switch (sourceType) {
        case TicketSourceType.MESSAGE:
          icon = "whatsapp.png";
          break;
      }
    }
    return iconPath + icon;
  }
  function objectLength(obj: object) {
    return Object.values(obj).filter((value) => {
      if (Array.isArray(value)) {
        return value.length > 0; // Exclude empty arrays
      } else if (typeof value === "object" && value !== null) {
        return Object.keys(value).length > 0; // Exclude empty objects
      } else if (typeof value === "string") {
        return value.trim() !== ""; // Exclude empty strings
      } else if (typeof value === "boolean") {
        return true;
      }
      return (
        value !== null &&
        value !== undefined &&
        (value !== false || typeof value === "number")
      ); // Handle other falsy values (e.g., null, undefined, false, 0)
    }).length;
  }
  /**
   * Filter-badge count. Same rules as objectLength, but the "match-all" key is
   * skipped: a multi-select's AND/OR match mode modifies another filter rather
   * than being one itself, so ticking it must not bump the badge.
   */
  function activeFilterCount(filterData: Record<string, any>) {
    return objectLength(
      Object.fromEntries(
        Object.entries(filterData).filter(([key]) => key !== "match-all"),
      ),
    );
  }

  function removeOne(index: number, list: array) {
    list.splice(index, 1);
  }
  function formatDate(dateString: string) {
    if (dateString == null) return null;

    const date = new Date(dateString);
    return new Intl.DateTimeFormat("default", { dateStyle: "long" }).format(
      date,
    );
  }

  function formatDateTime(dateString: string) {
    if (dateString == null) return null;

    const date = new Date(dateString);
    return new Intl.DateTimeFormat("default", {
      dateStyle: "long",
      timeStyle: "short",
    }).format(date);
  }

  function getFileType(fileName = "") {
    const fileExtension = fileName.split(".").pop();

    let fileType;
    switch (fileExtension) {
      case "jpg":
      case "jpeg":
      case "png":
        fileType = "image";
        break;
      case "pdf":
        fileType = "pdf";
        break;
      case "doc":
      case "docx":
        fileType = "word";
        break;
      case "xls":
      case "xlsx":
        fileType = "excel";
        break;
      default:
        fileType = "unknown";
        break;
    }

    return fileType;
  }

  function toTitleCase(str: string) {
    if (!str) {
      return "";
    }
    return str.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }

  function removeUnderscore(str: string) {
    if (!str) {
      return "";
    }
    return str.replace(/_/g, " ");
  }
  const getStatusClass = function (status: CallStatus) {
    let color;
    switch (status) {
      case CallStatus.STATUS_AGENT_SELECTED:
        color = "green-600";
        break;
      case CallStatus.STATUS_FAILED:
      case CallStatus.STATUS_MISSED:
        color = "red-600";
        break;
      case CallStatus.STATUS_REQUESTED:
      case CallStatus.STATUS_INITIATED:
        color = "violet-600";
        break;
      default:
        color = "dark-feature";
        break;
    }

    const fullClass = `text-${color} border-${color}`;
    return { [`${fullClass}`]: true };
  };

  const datePickerShortcuts = [
    {
      text: "A week ago",
      value: () => {
        const date = new Date();
        date.setTime(date.getTime() - 3600 * 1000 * 24 * 7);
        return date;
      },
    },
    {
      text: "Yesterday",
      value: () => {
        const date = new Date();
        date.setTime(date.getTime() - 3600 * 1000 * 24);
        return date;
      },
    },
    {
      text: "Today",
      value: new Date(),
    },
    {
      text: "Tomorrow",
      value: () => {
        const date = new Date();
        date.setTime(date.getTime() + 3600 * 1000 * 24);
        return date;
      },
    },
    {
      text: "A Week Later",
      value: () => {
        const date = new Date();
        date.setDate(date.getDate() + 7);
        return date;
      },
    },
  ];

  return {
    getBadgeColorForPriority,
    removeOne,
    getPlatformIcon,
    formatDate,
    formatDateTime,
    objectLength,
    activeFilterCount,
    getFileType,
    toTitleCase,
    getStatusClass,
    datePickerShortcuts,
    getDeliveryIntegrationIcon,
    removeUnderscore,
    getSourceTypeIcon,
  };
}
